import React from 'react';
import Button from './Button';
import BookingSummary, {
  SummaryArrow,
  SummaryFigure,
} from './BookingSummary';
import { BoxData } from '../models/APIModels';
import { SHIPPING_FEE } from '../constants';
import {
  monthlyCostToMoney,
  orderCostToMoney,
  purchaseCostToMoney,
  toMoney,
} from '../utilities/MoneyConversion';

interface BoxesSummaryProps {
  boxes: BoxData[];
  storageRequired: boolean;
  isSubmitting: boolean;
  deliveryFee: number;
}

// Own boxes ship at a premium over the standard per-box rate.
const OWN_BOX_SHIPPING_SURCHARGE = 500;

/**
 * Shipping is billed per box, with a premium on boxes the customer supplies
 * themselves. Same arithmetic as the panel this replaced.
 */
export function shippingCost(boxes: BoxData[]): number {
  const chargeable = boxes.filter((box) => box.chargeShipping);
  const ownBoxes = chargeable
    .filter((box) => box.name === 'Own box')
    .reduce((total, box) => total + box.count, 0);
  const otherBoxes =
    chargeable.reduce((total, box) => total + box.count, 0) - ownBoxes;

  return (
    SHIPPING_FEE * otherBoxes +
    (SHIPPING_FEE + OWN_BOX_SHIPPING_SURCHARGE) * ownBoxes
  );
}

/**
 * Order summary + primary action for the Boxes page — the page's single price
 * surface for both flows.
 *
 * Sticks to the bottom of the viewport while the catalogue scrolls, so the
 * running total and the way forward are never more than a glance away. The
 * page previously parked both at the very bottom of a nine-card grid: on a
 * phone that meant scrolling the whole list to find out what the selection
 * cost, and the totals row was `position: fixed` with no matching body
 * padding, so it sat on top of the last card.
 *
 * The shipping flow used to carry a second, quite different price surface as
 * well — a `total-shipping-cost-box` rail beside the catalogue, restating the
 * per-item costs the cards already show and ending in its own grand total.
 * Two places to look for one number. The breakdown that rail existed for
 * (items → shipping → empty-box delivery → order total) is folded in here as
 * figures, and the per-item repetition is dropped: the counts are on the
 * cards, and the itemised list belongs on Payment, where there is nothing
 * else to read it from.
 *
 * Figures are computed with the same helpers, and the same arguments, the
 * page used before — this bar re-presents those numbers, it does not
 * introduce a second pricing rule.
 */
export default function BoxesSummary({
  boxes,
  storageRequired,
  isSubmitting,
  deliveryFee,
}: BoxesSummaryProps): JSX.Element {
  const itemCount = boxes.reduce((total, box) => total + box.count, 0);

  // The empty-box delivery fee applies unless the order is nothing but own
  // boxes — mirrors what CalculateTotalCost did.
  const deliveryCostRequired = boxes.some(
    (box) => box.name !== 'Own box' && box.count > 0,
  );

  const figures: SummaryFigure[] = storageRequired
    ? [
        // Two figures of equal standing: paid now, and paid every month.
        // Neither is a sub-total of the other.
        { label: 'Materials', value: purchaseCostToMoney(boxes), headline: true },
        {
          label: 'Monthly storage',
          value: monthlyCostToMoney(boxes),
          headline: true,
        },
      ]
    : [
        { label: 'Items', value: purchaseCostToMoney(boxes) },
        { label: 'Shipping', value: toMoney(shippingCost(boxes)) },
        ...(deliveryCostRequired
          ? [{ label: 'Empty-box delivery', value: toMoney(deliveryFee) }]
          : []),
        {
          label: 'Order total',
          value: orderCostToMoney(boxes, deliveryCostRequired),
          headline: true,
        },
      ];

  return (
    <BookingSummary
      figures={figures}
      statusId="boxes-summary-count"
      status={
        itemCount === 0
          ? 'Add at least one item to continue'
          : `${itemCount} item${itemCount === 1 ? '' : 's'} selected`
      }
      action={
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={itemCount === 0}
          loading={isSubmitting}
          rightIcon={<SummaryArrow />}
          aria-describedby="boxes-summary-count"
        >
          Next
        </Button>
      }
      /*
        The page's fine print lives in the panel rather than in a block below
        it. It is the same copy, but it now sits with the figures it
        qualifies instead of under a nine-card grid nobody scrolls back up
        from — and it buys the vertical space the catalogue needs.
      */
      notes={
        storageRequired ? (
          <>
            25 kg maximum weight per box. Delivery of ordered boxes and
            materials: {toMoney(deliveryFee)}, within 1–3 working days of
            placing your order.
          </>
        ) : (
          <>
            25 kg maximum weight per box. Shipping is {toMoney(SHIPPING_FEE)} a
            box, or {toMoney(SHIPPING_FEE + OWN_BOX_SHIPPING_SURCHARGE)} for
            boxes you supply yourself.
          </>
        )
      }
    />
  );
}
